import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import UserSelectModal from './UserSelectModal';
import AdminPinModal from '../../components/AdminPinModal/AdminPinModal';
import ConsentRequestModal from '../../components/ConsentRequestModal/ConsentRequestModal';
import './Login.css';
import './UserSelectModal.css';
import API_BASE_URL from '../../config/api';



/* ─── helpers ────────────────────────────────────────────────────────────── */
const getDashboardLink = (role) => {
  const r = role?.toLowerCase();
  if (r === 'admin') return '/dashboard-admin';
  if (r === 'atleta') return '/dashboard-atleta';
  if (r === 'treinador') return '/dashboard-treinador';
  return '/dashboard-socio';
};

/* ─── Login page ─────────────────────────────────────────────────────────── */
const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // post-login state
  const [showUserSelect, setShowUserSelect] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingLink, setPendingLink] = useState(null);

  // Consent modal state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [targetConsentUser, setTargetConsentUser] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  /* Called once login API succeeds */
  const afterLogin = (data) => {
    const roles = data.roles || ['User'];
    const primaryRole = roles.find(r => ['Admin', 'Coordenador', 'Coordinator', 'Atleta', 'Treinador'].includes(r)) || roles[0] || 'User';

    localStorage.setItem('token', data.token);
    localStorage.setItem('roles', JSON.stringify(roles));
    localStorage.setItem('userType', primaryRole);
    localStorage.setItem('userName', `${data.firstName} ${data.lastName}`);
    localStorage.setItem('userEmail', data.email);
    localStorage.setItem('userId', data.id);
    localStorage.setItem('primaryUserId', data.id);
    localStorage.setItem('acceptedRegulation', data.acceptedRegulation ? 'true' : 'false');
    window.dispatchEvent(new CustomEvent('check-regulation'));

    const linked = data.linkedUsers || [];
    if (linked.length > 0) {
      localStorage.setItem('linkedUsers', JSON.stringify(linked));
    } else {
      localStorage.removeItem('linkedUsers');
    }

    if (!data.acceptedRegulation) return; // regulation modal will show

    // Build user list to display: ONLY users matching primary email or alias
    const me = { id: data.id, firstName: data.firstName, lastName: data.lastName, roles, hasCoordinatorProfile: data.hasCoordinatorProfile, hasCoachProfile: data.hasCoachProfile, hasAthleteProfile: data.hasAthleteProfile, hasMemberProfile: data.hasMemberProfile, isCurrentUser: true };
    const others = linked.filter(u => u.id !== data.id && u.isAliasMatch !== false).map(u => ({ ...u, isCurrentUser: false }));
    const combined = [me, ...others];

    if (combined.length === 1) {
      // Only one profile — go directly
      navigateAfterLogin(roles, combined[0], data);
    } else {
      // Multiple profiles — show picker
      setAllUsers(combined);
      setShowUserSelect(true);
    }
  };

  const navigateAfterLogin = (roles, _user, _data) => {
    const searchParams = new URLSearchParams(window.location.search);
    const redirectPath = searchParams.get('redirect');
    if (redirectPath) { window.location.href = redirectPath; return; }

    const rolesLower = (roles || []).map(r => typeof r === 'string' ? r.toLowerCase() : (r.name ? r.name.toLowerCase() : ''));
    if (rolesLower.includes('atleta')) window.location.href = '/dashboard-atleta';
    else if (rolesLower.includes('treinador')) window.location.href = '/dashboard-treinador';
    else if (rolesLower.includes('coordenador') || rolesLower.includes('coordinator')) window.location.href = '/dashboard-coordenador';
    else if (rolesLower.includes('admin')) window.location.href = '/dashboard-admin';
    else window.location.href = '/dashboard-socio';
  };

  const handleUserSelect = ({ user, role, link, requiresPin }) => {
    setShowUserSelect(false);
    localStorage.setItem('primaryUserId', user.id.toString());

    if (requiresPin || role === 'admin' || role === 'treinador') {
      // Ask for PIN before entering admin or treinador profile
      setPendingUserId(user.id);
      setPendingLink(link);
      setShowPinModal(true);
    } else {
      // Switch directly to that user profile
      const currentId = parseInt(localStorage.getItem('userId'));
      if (user.id !== currentId) {
        switchAndNavigate(user.id, link);
      } else {
        window.location.href = link;
      }
    }
  };

  const switchAndNavigate = async (targetUserId, link) => {
    try {
      const token = localStorage.getItem('token');
      const primaryUserId = localStorage.getItem('primaryUserId');
      const res = await fetch(`${API_BASE_URL}/api/auth/switch-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Primary-User-Id': primaryUserId || ''
        },
        body: JSON.stringify({ targetUserId, primaryUserId: primaryUserId ? parseInt(primaryUserId) : null }),
      });
      if (res.ok) {
        const d = await res.json();
        localStorage.setItem('token', d.token);
        localStorage.setItem('roles', JSON.stringify(d.roles));
        localStorage.setItem('userName', `${d.firstName} ${d.lastName}`);
        localStorage.setItem('userEmail', d.email);
        localStorage.setItem('userId', d.id.toString());
        localStorage.setItem('acceptedRegulation', d.acceptedRegulation ? 'true' : 'false');
        if (d.linkedUsers) localStorage.setItem('linkedUsers', JSON.stringify(d.linkedUsers));
      }
    } catch (e) { console.error('Switch error', e); }
    window.location.href = link;
  };

  const handlePinSuccess = () => {
    setShowPinModal(false);
    // Switch to the admin user now that PIN is verified
    switchAndNavigate(pendingUserId, pendingLink || '/dashboard-admin');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login falhou');
      afterLogin(data);
    } catch (err) {
      setError(err.message || 'Email ou password incorretos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="login-page">
        <div className="login-container">
          <div className="login-branding">
            <img src="/CDP_logo.png" alt="CDP Logo" className="login-logo" />
            <h1>Clube Desportivo Da Póvoa</h1>
          </div>

          <div className="login-form-wrapper">
            <form onSubmit={handleSubmit} className="login-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label>Email</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="seu.email@exemplo.com"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="auth-links">
                <Link to="/recuperar-password">Esqueceu-se da password?</Link>
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'A entrar...' : 'Entrar'}
              </button>

              <div className="register-link">
                <p>Ainda não é sócio? <Link to="/registo">Registe-se aqui</Link></p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Post-login profile selector */}
      <UserSelectModal
        isOpen={showUserSelect}
        users={allUsers}
        onSelect={handleUserSelect}
        onClose={() => setShowUserSelect(false)}
      />

      {/* Admin PIN gate */}
      <AdminPinModal
        isOpen={showPinModal}
        targetUserId={pendingUserId}
        onSuccess={handlePinSuccess}
        onClose={() => { setShowPinModal(false); setShowUserSelect(true); }}
      />

      {/* RGPD Consent Modal */}
      <ConsentRequestModal
        isOpen={showConsentModal}
        targetUser={targetConsentUser}
        onClose={() => { setShowConsentModal(false); setShowUserSelect(true); }}
      />
    </>
  );
};

export default Login;